import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ClipboardList, ChevronRight, CheckCircle2, Play, Clock, AlertTriangle, XCircle, Warehouse } from 'lucide-react'
import { api } from '@/lib/api'

type Inv = {
  id: number
  codigo: string
  descricao: string | null
  deposito_nome: string | null
  status: string
  total_itens: number
  total_contados: number
  total_divergentes: number
  created_at: string
}

const STATUS_MAP: Record<string, { label: string; cls: string; icon: any }> = {
  rascunho:    { label: 'Rascunho',    cls: 'bg-ink-100 text-ink-700',         icon: Clock },
  em_contagem: { label: 'Em contagem', cls: 'bg-blue-100 text-blue-700',       icon: Play },
  conferencia: { label: 'Conferência', cls: 'bg-amber-100 text-amber-700',     icon: AlertTriangle },
  finalizado:  { label: 'Finalizado',  cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  aprovado:    { label: 'Aprovado',    cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  cancelado:   { label: 'Cancelado',   cls: 'bg-red-100 text-red-700',         icon: XCircle },
}

export default function InventariosMobilePage() {
  const navigate = useNavigate()
  const { data: invs = [], isLoading } = useQuery<Inv[]>({
    queryKey: ['inventarios-m'],
    queryFn: async () => (await api.get('/inventarios')).data,
  })

  const ordenados = [...invs].sort((a, b) => {
    const score = (s: string) =>
      s === 'em_contagem' ? 0 : s === 'conferencia' ? 1 : s === 'rascunho' ? 2 : 3
    return score(a.status) - score(b.status)
  })

  const ativos = invs.filter(i => ['em_contagem', 'conferencia'].includes(i.status)).length

  return (
    <div className="space-y-3">
      <div className="text-center">
        <ClipboardList size={32} className="inline-block text-brand-600 mb-1" />
        <h2 className="text-lg font-bold">Inventários</h2>
        {ativos > 0 && (
          <p className="text-sm text-blue-700 font-medium">{ativos} em andamento</p>
        )}
      </div>

      {isLoading && <div className="text-center text-ink-500 py-8">Carregando...</div>}

      {!isLoading && invs.length === 0 && (
        <div className="card card-body text-center text-ink-500">
          Nenhum inventário.<br />
          <span className="text-xs">Crie pelo desktop primeiro.</span>
        </div>
      )}

      {ordenados.map(inv => {
        const cfg = STATUS_MAP[inv.status] || STATUS_MAP.rascunho
        const Icon = cfg.icon
        const pct = inv.total_itens > 0 ? Math.round((inv.total_contados / inv.total_itens) * 100) : 0
        const ativo = ['em_contagem', 'conferencia'].includes(inv.status)
        return (
          <button
            key={inv.id}
            onClick={() => navigate(`/m/inventarios/${inv.id}`)}
            className={`w-full text-left bg-white rounded-lg p-3 shadow-sm border ${
              ativo ? 'border-blue-300 ring-1 ring-blue-200' : 'border-ink-200'
            } active:bg-ink-50 flex gap-3 items-center`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
              <Icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <span className="font-mono">{inv.codigo}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${cfg.cls}`}>{cfg.label}</span>
              </div>
              {inv.descricao && <div className="text-xs text-ink-600 truncate">{inv.descricao}</div>}
              <div className="text-xs text-ink-500 flex items-center gap-2 mt-0.5">
                <span className="flex items-center gap-1"><Warehouse size={11} /> {inv.deposito_nome}</span>
                <span>·</span>
                <span>{inv.total_contados}/{inv.total_itens} ({pct}%)</span>
                {inv.total_divergentes > 0 && (
                  <><span>·</span><span className="text-amber-700">{inv.total_divergentes} div</span></>
                )}
              </div>
            </div>
            <ChevronRight size={18} className="text-ink-400 flex-shrink-0" />
          </button>
        )
      })}
    </div>
  )
}
